/**
 * RefreshSettingsStep - Step 5 of view creation wizard
 *
 * Configure refresh settings and view type (virtual vs materialized)
 */

import React from 'react';
import { HardDrive, Zap } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import type { ViewFormData } from '@/pages/ViewCreatePage';

interface RefreshSettingsStepProps {
  formData: ViewFormData;
  updateFormData: (updates: Partial<ViewFormData>) => void;
}

/**
 * Get description for refresh schedule
 */
const getScheduleDescription = (schedule: string): string => {
  switch (schedule) {
    case 'on_ingestion':
      return 'View updates automatically whenever new files are ingested';
    case 'hourly':
      return 'View refreshes every hour at the top of the hour';
    case 'daily':
      return 'View refreshes once per day at 12:00 AM UTC';
    case 'weekly':
      return 'View refreshes once per week on Sunday at 12:00 AM UTC';
    case 'manual':
      return 'View only refreshes when manually triggered';
    default:
      return '';
  }
};

export const RefreshSettingsStep: React.FC<RefreshSettingsStepProps> = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6">
      {/* Auto-Refresh Toggle */}
      <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" />
            <Label htmlFor="auto-refresh" className="cursor-pointer">
              Auto-Refresh
            </Label>
          </div>
          <p className="text-sm text-muted-foreground">
            Automatically refresh when matching files change
          </p>
        </div>
        <Switch
          id="auto-refresh"
          checked={formData.is_auto_refresh}
          onCheckedChange={(checked) => updateFormData({ is_auto_refresh: checked })}
        />
      </div>

      {/* Refresh Schedule */}
      {formData.is_auto_refresh && (
        <div>
          <Label htmlFor="refresh-schedule">Refresh Schedule</Label>
          <Select
            value={formData.refresh_schedule}
            onValueChange={(value: any) => updateFormData({ refresh_schedule: value })}
          >
            <SelectTrigger id="refresh-schedule" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="on_ingestion">On Ingestion (Real-time)</SelectItem>
              <SelectItem value="hourly">Hourly</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="manual">Manual Only</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground mt-2">
            {getScheduleDescription(formData.refresh_schedule)}
          </p>
        </div>
      )}

      {/* View Type */}
      <div>
        <Label className="mb-3 block">View Type</Label>
        <RadioGroup
          value={formData.view_type}
          onValueChange={(value: 'virtual' | 'materialized') => updateFormData({ view_type: value })}
        >
          {/* Virtual View */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="virtual" id="virtual" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="virtual" className="font-medium cursor-pointer">
                Virtual View
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Queries data directly from source files on demand. No additional storage required.
                Best for infrequently accessed data or when storage is a concern.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">
                  No Storage Cost
                </span>
                <span className="text-xs bg-info/10 text-info px-2 py-1 rounded">
                  Always Fresh
                </span>
                <span className="text-xs bg-warning/10 text-warning px-2 py-1 rounded">
                  Slower Queries
                </span>
              </div>
            </div>
          </div>

          {/* Materialized View */}
          <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
            <RadioGroupItem value="materialized" id="materialized" className="mt-1" />
            <div className="flex-1">
              <Label htmlFor="materialized" className="font-medium cursor-pointer">
                Materialized View
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Pre-computes and stores results as Parquet files for fast query performance.
                Requires additional storage. Best for frequently accessed data and complex queries.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">
                  Fast Queries
                </span>
                <span className="text-xs bg-info/10 text-info px-2 py-1 rounded">
                  Optimized Storage
                </span>
                <span className="text-xs bg-warning/10 text-warning px-2 py-1 rounded">
                  Uses Storage
                </span>
              </div>
            </div>
          </div>
        </RadioGroup>
      </div>

      {/* Storage Location Preview */}
      {formData.view_type === 'materialized' && formData.name && (
        <Alert>
          <HardDrive className="h-4 w-4" />
          <AlertTitle>Storage Location</AlertTitle>
          <AlertDescription className="font-mono text-xs mt-2">
            /lakehouse/views/{formData.name}/data.parquet
          </AlertDescription>
          <AlertDescription className="text-xs mt-2">
            Materialized data will be stored at this location
          </AlertDescription>
        </Alert>
      )}

      {/* Help Text */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <h4 className="font-medium mb-2">Choosing the Right View Type</h4>
        <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div>
            <h5 className="font-medium text-foreground mb-1">Use Virtual When:</h5>
            <ul className="space-y-1 list-disc list-inside">
              <li>Data changes frequently</li>
              <li>View is queried rarely</li>
              <li>Storage space is limited</li>
              <li>Always need fresh data</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-foreground mb-1">Use Materialized When:</h5>
            <ul className="space-y-1 list-disc list-inside">
              <li>View is queried frequently</li>
              <li>Query performance is critical</li>
              <li>Complex aggregations needed</li>
              <li>Storage space is available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
